import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'

import { ConfirmationOptions, ConfirmationServiceProps } from '../types'
import { ConfirmationDialog } from './ConfirmationDialog'
import { ConfirmationDialogDescription } from './ConfirmationDialogDescription'
import { ConfirmationDialogFooter } from './ConfirmationDialogFooter'

const ConfirmationServiceContext = createContext<{
	closeConfirmModal: () => void
	openConfirmModal: (
		options: ConfirmationOptions,
	) => Promise<ConfirmationOptions | null>
}>({
	openConfirmModal: () => Promise.reject(),
	closeConfirmModal: () => {},
})

export const useConfirmationModal = () => useContext(ConfirmationServiceContext)

export const ConfirmationServiceProvider = ({
	children,
}: ConfirmationServiceProps) => {
	const [confirmationState, setConfirmationState] =
		useState<ConfirmationOptions | null>(null)
	const [isProcessing, setIsProcessing] = useState(false)
	const hasAutoStartedRef = useRef(false)
	const awaitingPromiseRef = useRef<{
		reject: () => void
		resolve: (options: ConfirmationOptions | null) => void
	}>()

	const openConfirmModal = useCallback((options: ConfirmationOptions) => {
		setConfirmationState(options)
		if (!options.preserveProcessingState) {
			setIsProcessing(false)
			hasAutoStartedRef.current = false
		}

		return new Promise<ConfirmationOptions | null>((resolve, reject) => {
			awaitingPromiseRef.current = { resolve, reject }
		})
	}, [])

	const handleClose = () => {
		if (awaitingPromiseRef.current) {
			if (confirmationState?.taskState === 'FINISHED') {
				awaitingPromiseRef.current.resolve(
					confirmationState ? { ...confirmationState } : null,
				)
			} else if (confirmationState?.catchOnCancel) {
				awaitingPromiseRef.current.reject()
			}
		}

		setConfirmationState(null)
		setIsProcessing(false)
		hasAutoStartedRef.current = false
	}

	const closeConfirmModal = useCallback(() => {
		setConfirmationState(null)
		setIsProcessing(false)
		hasAutoStartedRef.current = false
	}, [])

	const handleSubmit = useCallback(() => {
		if (awaitingPromiseRef.current) {
			const options = confirmationState ? { ...confirmationState } : null

			awaitingPromiseRef.current.resolve(options)
		}

		if (confirmationState?.isAsync) {
			setIsProcessing(true)
		} else {
			setIsProcessing(false)
			setConfirmationState(null)
		}
	}, [confirmationState])

	useEffect(() => {
		if (
			confirmationState?.autoStart &&
			confirmationState?.isAsync &&
			!isProcessing &&
			!hasAutoStartedRef.current
		) {
			hasAutoStartedRef.current = true
			handleSubmit()
		}
	}, [
		confirmationState?.autoStart,
		confirmationState?.isAsync,
		isProcessing,
		handleSubmit,
	])

	return (
		<>
			<ConfirmationServiceContext.Provider
				value={{ openConfirmModal, closeConfirmModal }}
			>
				{children}
			</ConfirmationServiceContext.Provider>

			<ConfirmationDialog
				open={Boolean(confirmationState)}
				onSubmit={handleSubmit}
				onClose={handleClose}
				title={confirmationState?.title || ''}
				description={
					<ConfirmationDialogDescription
						confirmationState={confirmationState}
						isProcessingStarted={isProcessing}
					/>
				}
				footer={
					<ConfirmationDialogFooter
						confirmationState={confirmationState}
						handleClose={handleClose}
						handleSubmit={handleSubmit}
						isProcessingStarted={isProcessing}
					/>
				}
			/>
		</>
	)
}
